/**
 * Dev21 — a fresh, modern, premium digital invitation template.
 *
 * A fully data-driven, single-page experience: every section is an
 * independent, reusable component fed from a resolved `Dev21Content` object.
 * The template self-registers with the shared template `registry` on import.
 *
 * Section order:
 *   Hero → Couple → Story → Gallery → Video → Family → Schedule → Venue
 *   → RSVP → Footer
 */

import React from 'react';
import { registry } from '../registry';
import type { InvitationData, TemplateMetadata } from '../types';
import { resolveContent } from './content';
import { dev21Theme, themeCssVars } from './theme';
import { HeroSection } from './sections/HeroSection';
import { CoupleSection } from './sections/CoupleSection';
import { StoryTimelineSection } from './sections/StoryTimelineSection';
import { GallerySection } from './sections/GallerySection';
import { VideoSection } from './sections/VideoSection';
import { FamilySection } from './sections/FamilySection';
import { ScheduleSection } from './sections/ScheduleSection';
import { VenueSection } from './sections/VenueSection';
import { RsvpSection } from './sections/RsvpSection';
import { FooterSection } from './sections/FooterSection';

const META: TemplateMetadata = {
  id: 'dev21',
  name: 'Dev21 — Modern Premium',
  occasionType: 'WEDDING',
  description:
    'A fresh, cinematic single-page invitation: parallax hero, animated story timeline, masonry gallery, teaser video, family tree, schedule, live map and a validated RSVP.',
  isPremium: true,
  tags: ['modern', 'premium', 'animated', 'glassmorphism', 'responsive'],
  colorScheme: {
    primary: dev21Theme.accent,
    accent: dev21Theme.accentSoft,
    bg: dev21Theme.cream,
  },
};

export function Dev21Template({ data }: { data: InvitationData }) {
  const content = React.useMemo(() => resolveContent(data), [data]);
  const invitationId = data.invitationId ?? data.id ?? null;
  const isLive = data.status === 'PUBLISHED' || data.status === 'PROCESSING';

  /** Smooth-scroll to the RSVP section (used by the hero CTA). */
  const scrollToRsvp = React.useCallback(() => {
    document
      .getElementById('rsvp')
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  return (
    <main
      className="dev21-root min-h-screen scroll-smooth bg-[var(--d21-cream)] font-sans text-[var(--d21-ink)] antialiased"
      style={themeCssVars(dev21Theme)}
    >
      <HeroSection
        content={{ ...content.hero, announcement: content.announcement }}
        onRsvp={scrollToRsvp}
      />
      <CoupleSection content={content.couple} />
      <StoryTimelineSection content={content.story} />
      <GallerySection content={content.gallery} />
      <VideoSection content={content.video} />
      <FamilySection content={content.family} />
      <ScheduleSection content={content.schedule} />
      <VenueSection content={content.venue} />
      <RsvpSection content={content.rsvp} invitationId={invitationId} isLive={isLive} />
      <FooterSection content={content.footer} />
    </main>
  );
}

registry.register({ metadata: META, component: Dev21Template });
